// Phase E — Dashboard Widget Registry
// Each entry describes a pinnable section of the Dashboard. The Dashboard
// only renders a widget when its id appears in the user's `pins` set.
//
// Defaults are calculated per role; the user can always override the
// pinned set via the Pin-Picker modal. The "Hero" greeting + KPI strip
// are NOT pinnable — they are always shown as the dashboard anchor.
//
// Phase E-2 (26.05.2026) — preferences sync across devices via
// /api/me/dashboard-prefs. localStorage is kept as fast-path cache.

import { api } from "@/lib/api";

export const WIDGETS = [
  {
    id: "firm-stats",
    label: "Firmen-Übersicht",
    description: "Aktive MA · Fahrzeuge · Stunden · offene Aufträge pro Firma",
    permission: "dashboard",
    defaultRoles: ["admin", "geschaeftsfuehrung"],
  },
  {
    id: "hr-summary",
    label: "HR · Personal-Übersicht",
    description: "Resturlaub gesamt, Krankenstände, Pending-Anträge, Dokumente-Expiring",
    permission: "abwesenheiten",
    defaultRoles: ["admin", "geschaeftsfuehrung", "disponent"],
  },
  {
    id: "expiring-vehicles",
    label: "Fahrzeug-Termine",
    description: "Überfällige & bald ablaufende Pickerl/Versicherung/Vignette",
    permission: "fuhrpark",
    defaultRoles: ["admin", "geschaeftsfuehrung", "fuhrparkleiter", "disponent"],
  },
  {
    id: "expiring-driver-docs",
    label: "Fahrer-Dokumente · ablaufend",
    description: "Welcher Fahrer · welches Dokument · sofort aktualisierbar",
    permission: "dokumente",
    defaultRoles: ["admin", "geschaeftsfuehrung", "disponent", "abteilungsleiter"],
  },
  {
    id: "queues",
    label: "Genehmigungs-Queue",
    description: "Offene Abwesenheiten, Profil-Anfragen, Zeit-Anfragen",
    // Any-of: user sees the widget if they can view at least one of the queues.
    permission: ["abwesenheiten", "profilanfragen", "audit"],
    defaultRoles: ["admin", "geschaeftsfuehrung", "disponent", "backoffice"],
  },
  {
    id: "finance",
    label: "Finanzen",
    description: "Offene & überfällige Rechnungen, Zahlungs-Status",
    permission: "finanzen",
    defaultRoles: ["admin", "geschaeftsfuehrung", "backoffice", "buchhalter"],
  },
  {
    id: "activity",
    label: "Letzte Aktivitäten",
    description: "Audit-Log der letzten 8 Aktionen",
    permission: "audit",
    defaultRoles: ["admin", "geschaeftsfuehrung"],
  },
  {
    id: "departments",
    label: "Performance pro Firma",
    description: "MA · KFZ · Compliance · offene Aufgaben aggregiert pro Firma",
    permission: "dashboard",
    defaultRoles: ["admin", "geschaeftsfuehrung", "abteilungsleiter"],
  },
  {
    id: "payroll-preview",
    label: "Lohnlauf-Vorschau",
    description:
      "Voraussichtliche Bruttosumme · Diäten · Überstunden je Fahrer für den laufenden Monat. Anomalien (fehlende Tage, fehlende KV-Kategorie) sofort sichtbar.",
    permission: "dashboard",
    defaultRoles: ["admin", "geschaeftsfuehrung", "buchhalter"],
  },
];

const STORAGE_KEY = (userId) => `munja.dashboard_pins.${userId || "anon"}`;

/** Returns the user's effective pin set (Set<string>). */
export function loadPins(user) {
  if (!user) return new Set();
  try {
    const raw = localStorage.getItem(STORAGE_KEY(user.id));
    if (raw) return new Set(JSON.parse(raw));
  } catch { /* fall through */ }
  // First visit → default set for role
  const role = user.role;
  const defaults = WIDGETS.filter((w) => w.defaultRoles.includes(role)).map((w) => w.id);
  if (defaults.length > 0) return new Set(defaults);
  // Unknown / custom role → minimal fallback so dashboard isn't blank.
  return new Set(["queues", "activity"]);
}

/** Persists the pin set for a given user. */
export function savePins(user, pinsSet) {
  if (!user) return { ok: false, reason: "no_user" };
  try {
    localStorage.setItem(STORAGE_KEY(user.id), JSON.stringify([...pinsSet]));
    return { ok: true };
  } catch (e) {
    // QuotaExceededError or storage disabled (e.g. private browsing)
    return { ok: false, reason: e?.name || "storage_error" };
  }
}

/** Returns the widgets the current user is *allowed* to pin. A widget's
 * `permission` may be a single key (string) OR an array of keys (any-of).
 */
export function availableWidgets(user, can) {
  if (user?.role === "admin" || user?.is_super_admin) return WIDGETS;
  return WIDGETS.filter((w) => {
    if (!w.permission) return true;
    if (Array.isArray(w.permission)) {
      return w.permission.some((p) => can(p, "view"));
    }
    return can(w.permission, "view");
  });
}

// ─── Phase E-2 — Server sync ──────────────────────────────────────────
/**
 * Fetches the user's pin set from the server. Falls back gracefully to
 * the localStorage cache (or role-default) on any error. On success,
 * the localStorage cache is refreshed so subsequent fast-loads match.
 */
export async function fetchServerPins(user) {
  if (!user) return new Set();
  try {
    const { data } = await api.get("/me/dashboard-prefs");
    const serverPins = Array.isArray(data?.pins) ? data.pins : [];
    if (data?.updated_at) {
      // Server has a record — trust it, refresh cache.
      const set = new Set(serverPins);
      try {
        localStorage.setItem(STORAGE_KEY(user.id), JSON.stringify([...set]));
      } catch { /* cache write failed, non-fatal */ }
      return set;
    }
    // Server has no record yet → keep using local default.
    return loadPins(user);
  } catch {
    // Offline / unauthenticated / 5xx — local cache is good enough.
    return loadPins(user);
  }
}

/**
 * Persists the pin set both locally (instant) and to the server (best-effort).
 * Returns `{ok, reason, synced}` so the UI can show a clear toast.
 */
export async function savePinsSynced(user, pinsSet) {
  const local = savePins(user, pinsSet);
  if (!user) return { ...local, synced: false };
  try {
    await api.put("/me/dashboard-prefs", { pins: [...pinsSet] });
    return { ...local, synced: true };
  } catch {
    // Local save still succeeded — surface that to the caller.
    return { ...local, synced: false };
  }
}
